import type { DeliveryZone } from '@choco-admin/types';

import { findDeliveryZoneError } from './deliveryZones';

/**
 * A hand-drawn area held aside so it can be added to another distributor.
 *
 * Legacy admin let CS copy an area from one distributor and create it on the
 * next, which is how identical boundaries get shared between distributors that
 * serve the same patch. The ready-made library does not replace it: a boundary
 * someone drew by hand is not in the library, and redrawing it by eye does not
 * reproduce it.
 *
 * `sessionStorage`, because the copy has to survive navigating to the other
 * distributor, and because it is scratch data with no business meaning past the
 * tab it was copied in.
 */
const STORAGE_KEY = 'choco-portal:copied-delivery-zone';

/** Reads whatever was copied, or null when there is nothing usable. */
export const readCopiedZone = (): DeliveryZone | null => {
  let raw: string | null = null;

  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Copying is a
    // convenience, so losing it is not worth an error on screen.
    return null;
  }

  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const { name, points } = parsed as Partial<DeliveryZone>;
    if (typeof name !== 'string' || !Array.isArray(points)) return null;

    const zone: DeliveryZone = {
      name,
      points: points.filter(
        (point): point is DeliveryZone['points'][number] =>
          typeof point?.latitude === 'number' &&
          typeof point?.longitude === 'number'
      ),
    };

    // Same validation a drawn area goes through, so a copy from an older build
    // or a hand-edited value cannot be pasted into a shape the backend rejects.
    return findDeliveryZoneError({ zone, otherZones: [] }) ? null : zone;
  } catch {
    return null;
  }
};

export const writeCopiedZone = (zone: DeliveryZone): void => {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ name: zone.name, points: zone.points })
    );
  } catch {
    // See readCopiedZone.
  }
};

/**
 * A name that does not collide with what the distributor already has.
 *
 * Zone names must be unique per distributor, and the usual reason to copy an
 * area is that the other distributor covers the same ground, so a clash is the
 * normal case rather than the exception.
 *
 * Compared without case, because that is how the save checks it: "Berlin" and
 * "berlin" are the same name to a human reading the list, so pasting one onto
 * the other would produce an area the copy flow cannot save.
 */
export const uniqueZoneName = (
  name: string,
  existing: DeliveryZone[]
): string => {
  const taken = new Set(existing.map((zone) => zone.name.trim().toLowerCase()));
  const base = name.trim() || 'Copied area';

  if (!taken.has(base.toLowerCase())) return base;

  for (let suffix = 2; suffix < 100; suffix += 1) {
    const candidate = `${base} (${suffix})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }

  return base;
};
